<template>
  <section
    class="article-chat"
    :class="{
      'article-chat--raised': raised,
      'article-chat--sections': goalListOpen,
    }"
    :style="raisedStyle"
    data-test="article-chat-topic"
    :data-raised="raised ? 'true' : 'false'"
    @keydown="onKeydown"
    @focusin="onFocusIn"
  >
    <div class="article-chat__stage">
      <div class="article-chat__top">
        <div
          v-if="showHistory"
          class="article-chat__history"
          role="log"
          aria-live="polite"
          data-test="article-chat-history"
        >
          <p
            v-if="historyView.folded"
            class="article-chat__history-row"
          >
            <span>✓ {{ $t('articleChat.stepsReady', [historyView.from, historyView.to]) }}</span>
            <button
              type="button"
              class="article-chat__text-button"
              data-test="article-chat-show-entered"
              @click="panelOpen = true"
            >
              {{ $t('articleChat.showEntered') }}
            </button>
          </p>
          <template v-else>
            <p
              v-for="row in historyView.visible"
              :key="row.id"
              class="article-chat__history-row"
            >
              <span>✓ {{ row.step }} {{ row.title }} · {{ row.value }}</span>
              <button
                type="button"
                class="article-chat__text-button"
                :data-test="'article-chat-change-' + row.id"
                @click="changeStep(row.id)"
              >
                {{ $t('articleChat.change') }}
              </button>
            </p>
            <button
              v-if="panelRows.length"
              type="button"
              class="article-chat__text-button article-chat__history-link"
              data-test="article-chat-show-entered"
              @click="panelOpen = true"
            >
              {{ $t('articleChat.showEntered') }}
            </button>
          </template>
        </div>
        <p
          v-if="asking && !resumeOffer"
          class="article-chat__asking"
          data-test="article-chat-asking"
          aria-live="polite"
        >
          <span class="article-chat__sr">{{ $t('articleChat.asking') }}</span>
          <span
            class="article-chat__dots"
            aria-hidden="true"
          >
            <span />
            <span />
            <span />
          </span>
        </p>
        <div
          v-else-if="!resumeOffer"
          class="article-chat__question"
          data-test="article-chat-question"
          aria-live="polite"
        >
          <template v-if="mode === 'url'">
            <p class="article-chat__greeting">
              {{ $t('articleChat.greetingAnalyze') }}
            </p>
            <p class="mb-0">
              <strong>{{ $t('articleChat.urlQuestion') }}</strong>
              {{ $t('articleChat.urlLead') }}
            </p>
            <p
              v-if="reading"
              class="article-chat__hint"
              data-test="article-chat-reading"
              aria-live="polite"
            >
              {{ $t('articleChat.loadedReading') }}
            </p>
          </template>
          <template v-else-if="mode === 'loaded'">
            <p class="mb-0">
              <strong>{{ $t('articleChat.loadedQuestion') }}</strong>
              {{ $t('articleChat.loadedLead') }}
            </p>
          </template>
          <template v-else-if="mode === 'topic'">
            <p
              v-if="!analyze"
              class="article-chat__greeting"
            >
              {{ $t('articleChat.greeting') }}
            </p>
            <p class="mb-0">
              <strong>{{ analyze ? $t('articleChat.topicQuestionAnalyze') : $t('articleChat.topicQuestion') }}</strong>
              <template v-if="!analyze">
                {{ $t('createBrief.steps.brief.help') }}
              </template>
            </p>
          </template>
          <p
            v-else-if="mode === 'locale'"
            class="mb-0"
          >
            <strong>{{ $t('articleChat.localeQuestion') }}</strong>
            {{ $t('articleChat.localeHint') }}
          </p>
          <template v-else-if="mode === 'keyword'">
            <p class="mb-0">
              <strong>{{ $t('articleChat.keywordQuestion') }}</strong>
              {{ $t('articleChat.keywordLead') }}
            </p>
            <p class="article-chat__hint">
              {{ $t('articleChat.keywordWhat') }}
            </p>
          </template>
          <p
            v-else-if="mode === 'template'"
            class="mb-0"
          >
            <strong>{{ $t('articleChat.templateQuestion') }}</strong>
            {{ $t('articleChat.templateLead') }}
          </p>
          <p
            v-else-if="mode === 'style'"
            class="mb-0"
          >
            <strong>{{ $t('articleChat.styleQuestion') }}</strong>
            {{ $t('articleChat.styleLead') }}
          </p>
          <p
            v-else-if="mode === 'goal'"
            class="mb-0"
          >
            <strong>{{ $t('articleChat.goalQuestion') }}</strong>
            {{ $t('articleChat.goalLead') }}
          </p>
          <template v-else>
            <p class="mb-0">
              {{ analyze ? $t('articleChat.summaryQuestionAnalyze') : $t('articleChat.summaryQuestion') }}
            </p>
            <ul
              class="article-chat__summary"
              data-test="article-chat-summary"
            >
              <li
                v-if="analyze"
                class="article-chat__history-row"
              >
                <span>{{ $t('articleChat.historyUrl') }} · {{ urlPreviewText }}</span>
                <button
                  type="button"
                  class="article-chat__text-button"
                  data-test="article-chat-summary-url"
                  @click="changeStep('url')"
                >
                  {{ $t('articleChat.change') }}
                </button>
              </li>
              <li
                v-if="loadedHeadings.length"
                class="article-chat__history-row"
              >
                <span>{{ $t('articleChat.historyLoaded') }} · {{ loadedPreviewText }}</span>
                <button
                  type="button"
                  class="article-chat__text-button"
                  data-test="article-chat-summary-loaded"
                  @click="changeStep('loaded')"
                >
                  {{ $t('articleChat.change') }}
                </button>
              </li>
              <li class="article-chat__history-row">
                <span>{{ $t('articleChat.historyTopic') }} · {{ topicPreviewText }}</span>
                <button
                  type="button"
                  class="article-chat__text-button"
                  data-test="article-chat-summary-topic"
                  @click="changeStep('topic')"
                >
                  {{ $t('articleChat.change') }}
                </button>
              </li>
              <li class="article-chat__history-row">
                <span>{{ $t('articleChat.localeLabel') }} · {{ countryName }} · {{ languageName }}</span>
                <button
                  type="button"
                  class="article-chat__text-button"
                  data-test="article-chat-summary-locale"
                  @click="changeStep('locale')"
                >
                  {{ $t('articleChat.change') }}
                </button>
              </li>
              <li class="article-chat__history-row">
                <span>{{ $t('articleChat.historyKeyword') }} · {{ keywordPreviewText }}</span>
                <button
                  type="button"
                  class="article-chat__text-button"
                  data-test="article-chat-summary-keyword"
                  @click="changeStep('keyword')"
                >
                  {{ $t('articleChat.change') }}
                </button>
              </li>
              <li class="article-chat__history-row">
                <span>{{ $t('articleChat.historyTemplate') }} · {{ templateTitle }}</span>
                <button
                  type="button"
                  class="article-chat__text-button"
                  data-test="article-chat-summary-template"
                  @click="changeStep('template')"
                >
                  {{ $t('articleChat.change') }}
                </button>
              </li>
              <li class="article-chat__history-row">
                <span>{{ $t('articleChat.historyStyle') }} · {{ stylePreview }}</span>
                <button
                  type="button"
                  class="article-chat__text-button"
                  data-test="article-chat-summary-style"
                  @click="changeStep('style')"
                >
                  {{ $t('articleChat.change') }}
                </button>
              </li>
              <li
                v-if="analyze"
                class="article-chat__history-row"
              >
                <span>{{ $t('articleChat.historyGoal') }} · {{ goalPreviewText }}</span>
                <button
                  type="button"
                  class="article-chat__text-button"
                  data-test="article-chat-summary-goal"
                  @click="changeStep('goal')"
                >
                  {{ $t('articleChat.change') }}
                </button>
              </li>
              <li class="article-chat__history-row">
                <span>{{ $t('articleChat.historySecondary') }} · {{ secondaryPreview }}</span>
                <button
                  type="button"
                  class="article-chat__text-button"
                  data-test="article-chat-summary-secondary"
                  @click="changeStep('style')"
                >
                  {{ $t('articleChat.change') }}
                </button>
              </li>
            </ul>
          </template>
        </div>
      </div>

      <div class="article-chat__center">
        <div
          v-if="resumeOffer"
          class="article-chat__resume"
          data-test="article-chat-resume"
        >
          <p class="article-chat__greeting">
            {{ $t('articleChat.resumeTitle') }}
          </p>
          <div class="article-chat__resume-actions">
            <button
              type="button"
              class="article-chat__continue article-chat__continue--accent"
              data-test="article-chat-resume-yes"
              @click="$emit('resume')"
            >
              {{ $t('articleChat.resumeYes') }}
            </button>
            <button
              type="button"
              class="article-chat__continue article-chat__continue--outline"
              data-test="article-chat-resume-restart"
              @click="$emit('restart')"
            >
              {{ $t('articleChat.resumeRestart') }}
            </button>
          </div>
        </div>
        <div
          v-else-if="mode === 'url'"
          class="article-chat__shell"
          data-test="article-chat-field"
        >
          <div class="article-chat__inner">
            <label
              class="article-chat__label"
              for="article-chat-url-input"
            >{{ $t('articleChat.urlLabel') }}</label>
            <input
              id="article-chat-url-input"
              ref="urlInput"
              class="article-chat__input article-chat__input--line"
              data-test="article-chat-url"
              type="url"
              inputmode="url"
              autocomplete="url"
              spellcheck="false"
              :value="url"
              aria-required="true"
              :aria-invalid="urlState === 'ready' ? 'false' : 'true'"
              :aria-describedby="urlDescribedBy"
              @input="onUrl"
            >
            <p
              id="article-chat-url-example"
              class="article-chat__hint"
            >
              {{ $t('articleChat.urlExample') }}
            </p>
            <p
              v-if="urlState === 'empty'"
              id="article-chat-url-empty"
              class="article-chat__empty"
              data-test="article-chat-url-empty"
            >
              {{ $t('articleChat.urlEmpty') }}
            </p>
            <p
              v-else-if="urlState !== 'ready'"
              id="article-chat-url-invalid"
              class="article-chat__error"
              data-test="article-chat-url-invalid"
            >
              {{ urlErrorText }}
            </p>
          </div>
        </div>
        <div
          v-else-if="mode === 'topic'"
          class="article-chat__shell"
          data-test="article-chat-field"
        >
          <div class="article-chat__inner">
            <label
              class="article-chat__label"
              for="article-chat-topic-input"
            >{{ $t('articleChat.topicLabel') }}</label>
            <textarea
              id="article-chat-topic-input"
              ref="prompt"
              class="article-chat__input"
              data-test="article-chat-prompt"
              rows="4"
              :value="value"
              :aria-invalid="fieldState === 'over' ? 'true' : 'false'"
              aria-describedby="article-chat-topic-status"
              @input="onInput"
            />
            <div
              id="article-chat-topic-status"
              class="article-chat__count"
              :class="{ 'article-chat__count--over': fieldState === 'over' }"
            >
              {{ $t('articleChat.wordCount', [wordCount, maxWords]) }}
            </div>
            <p
              v-if="fieldState === 'empty'"
              class="article-chat__empty"
              data-test="article-chat-empty"
            >
              {{ $t('articleChat.emptyHint') }}
            </p>
            <p
              v-else-if="fieldState === 'over'"
              class="article-chat__error"
              data-test="article-chat-over"
            >
              {{ $t('errors.maxWords', [maxWords]) }}
            </p>
          </div>
        </div>
        <div
          v-else-if="mode === 'keyword'"
          class="article-chat__shell"
          data-test="article-chat-field"
        >
          <div class="article-chat__inner">
            <label
              class="article-chat__label"
              for="article-chat-keyword-input"
            >{{ $t('articleChat.keywordLabel') }}</label>
            <input
              id="article-chat-keyword-input"
              ref="keyword"
              class="article-chat__input article-chat__input--line"
              data-test="article-chat-keyword"
              type="text"
              autocomplete="off"
              role="combobox"
              aria-autocomplete="list"
              :aria-expanded="suggestionListOpen ? 'true' : 'false'"
              aria-controls="article-chat-suggestion-list"
              :aria-activedescendant="activeSuggestionId || undefined"
              :value="keyword"
              aria-required="true"
              :aria-invalid="keywordReady ? 'false' : 'true'"
              :aria-describedby="keywordReady ? undefined : 'article-chat-keyword-empty'"
              @input="onKeyword"
            >
            <p
              v-if="!keywordReady"
              id="article-chat-keyword-empty"
              class="article-chat__empty"
              data-test="article-chat-keyword-empty"
            >
              {{ $t('articleChat.keywordEmpty') }}
            </p>
          </div>
        </div>
        <div
          v-else-if="mode === 'style'"
          class="article-chat__shell"
          data-test="article-chat-field"
        >
          <div class="article-chat__inner">
            <div class="article-chat__field-bar">
              <label
                class="article-chat__label"
                for="article-chat-secondary-input"
              >{{ $t('articleChat.styleLabel') }}</label>
              <span class="article-chat__count">{{ $t('createBrief.secondaryCount', [secondaryKeywords.length]) }}</span>
            </div>
            <div class="article-chat__secondary-row">
              <input
                id="article-chat-secondary-input"
                ref="secondary"
                class="article-chat__input article-chat__input--line"
                data-test="article-chat-secondary"
                type="text"
                autocomplete="off"
                :value="secondaryDraft"
                :placeholder="$t('createBrief.secondaryPlaceholder')"
                @input="onSecondaryDraft"
              >
              <button
                type="button"
                class="article-chat__add"
                data-test="article-chat-secondary-add"
                @click="$emit('secondary-add')"
              >
                {{ $t('createBrief.addKeyword') }}
              </button>
            </div>
          </div>
        </div>
        <div
          v-else
          class="article-chat__shell"
          data-test="article-chat-frame"
        >
          <div class="article-chat__inner">
            <p class="article-chat__label">
              {{ frameLabel }}
            </p>
            <button
              v-if="mode === 'locale'"
              ref="localeCard"
              type="button"
              class="article-chat__card article-chat__card--selected"
              data-test="article-chat-locale-card"
              aria-pressed="true"
              @click="openLocalePicker"
            >
              <span aria-hidden="true">✓</span>
              {{ countryName }} · {{ languageName }}
            </button>
            <div
              v-else-if="mode === 'template'"
              class="article-chat__cards"
              role="radiogroup"
              :aria-label="frameLabel"
            >
              <button
                v-for="id in templateIds"
                :key="id"
                type="button"
                role="radio"
                class="article-chat__card"
                :class="{ 'article-chat__card--selected': geoTemplate === id }"
                :aria-checked="geoTemplate === id ? 'true' : 'false'"
                :tabindex="templateTabIndex(id)"
                :data-test="'article-chat-template-' + id"
                @click="selectTemplate(id)"
              >
                <span class="article-chat__card-title">
                  <span
                    v-if="geoTemplate === id"
                    aria-hidden="true"
                  >✓</span>
                  {{ $t('createBrief.templates.' + id + '.title') }}
                </span>
                <span class="article-chat__card-blurb">{{ $t('createBrief.templates.' + id + '.blurb') }}</span>
              </button>
            </div>
            <div
              v-else-if="mode === 'loaded'"
              class="article-chat__loaded"
              data-test="article-chat-loaded"
            >
              <p
                class="article-chat__loaded-title"
                data-test="article-chat-loaded-title"
              >
                <span class="article-chat__loaded-kicker">{{ $t('articleChat.loadedTitleLabel') }}</span>
                {{ loadedTitleText }}
              </p>
              <p
                id="article-chat-loaded-outline-label"
                class="article-chat__loaded-kicker"
              >
                {{ $t('articleChat.loadedOutlineLabel') }}
              </p>
              <ol
                v-if="loadedHeadings.length"
                class="article-chat__outline"
                data-test="article-chat-loaded-outline"
                aria-labelledby="article-chat-loaded-outline-label"
              >
                <li
                  v-for="(item, index) in loadedHeadings"
                  :key="item.level + '-' + index"
                  class="article-chat__outline-item"
                  :data-level="item.level"
                  data-test="article-chat-loaded-heading"
                >
                  <span class="article-chat__outline-level">H{{ item.level }}</span>
                  <span class="article-chat__outline-text">{{ item.text }}</span>
                </li>
              </ol>
              <p
                class="article-chat__loaded-stay"
                data-test="article-chat-loaded-stays"
              >
                {{ $t('articleChat.loadedStays') }}
              </p>
            </div>
            <div
              v-else-if="mode === 'goal'"
              class="article-chat__goal"
              data-test="article-chat-goal"
            >
              <div
                class="article-chat__cards"
                role="radiogroup"
                :aria-label="frameLabel"
              >
                <button
                  v-for="id in goalIds"
                  :key="id"
                  type="button"
                  role="radio"
                  class="article-chat__card"
                  :class="{ 'article-chat__card--selected': improveGoal === id }"
                  :aria-checked="improveGoal === id ? 'true' : 'false'"
                  :tabindex="goalTabIndex(id)"
                  :data-test="'article-chat-goal-' + id"
                  @click="selectGoal(id)"
                >
                  <span class="article-chat__card-title">
                    <span
                      v-if="improveGoal === id"
                      aria-hidden="true"
                    >✓</span>
                    {{ $t('articleChat.goals.' + id + '.title') }}
                  </span>
                  <span class="article-chat__card-blurb">{{ $t('articleChat.goals.' + id + '.blurb') }}</span>
                </button>
              </div>
              <p
                v-if="goalListOpen"
                class="article-chat__goal-blurb"
              >
                {{ $t('articleChat.goals.' + improveGoal + '.blurb') }}
              </p>
              <div
                v-if="improveGoal === 'rewrite'"
                class="article-chat__goal-sections"
              >
                <p
                  id="article-chat-goal-sections-label"
                  class="article-chat__loaded-kicker"
                >
                  {{ $t('articleChat.goalSectionsLabel') }}
                </p>
                <ul
                  v-if="goalSectionList.length"
                  class="article-chat__sections"
                  data-test="article-chat-goal-sections"
                  aria-labelledby="article-chat-goal-sections-label"
                >
                  <li
                    v-for="section in goalSectionList"
                    :key="section.id"
                  >
                    <label class="article-chat__section">
                      <input
                        type="checkbox"
                        :checked="sectionOn(section.id)"
                        :data-test="'article-chat-section-' + section.id"
                        @change="toggleSection(section.id)"
                      >
                      <span>{{ section.text }}</span>
                    </label>
                  </li>
                </ul>
                <p
                  v-if="!goalSectionList.length"
                  class="article-chat__empty"
                  data-test="article-chat-goal-empty"
                >
                  {{ $t('articleChat.goalNoSections') }}
                </p>
                <p
                  v-else-if="!goalReady"
                  class="article-chat__empty"
                  data-test="article-chat-goal-need"
                >
                  {{ $t('articleChat.goalNeedSection') }}
                </p>
              </div>
            </div>
            <div
              v-else
              class="article-chat__choices"
              role="group"
              :aria-label="frameLabel"
            >
              <button
                ref="choiceImmediate"
                type="button"
                class="article-chat__choice article-chat__choice--accent"
                data-test="article-chat-generate-now"
                :disabled="asking || busy"
                @click="chooseWritePath('immediate')"
              >
                <span class="article-chat__choice-title">
                  <span aria-hidden="true">⚡</span>
                  {{ analyze ? $t('articleChat.improveNow') : $t('articleChat.generateNow') }}
                </span>
                <span class="article-chat__choice-line">{{ analyze ? $t('articleChat.improveNowLine') : $t('articleChat.generateNowLine') }}</span>
              </button>
              <button
                type="button"
                class="article-chat__choice article-chat__choice--outline"
                data-test="article-chat-customize-first"
                :disabled="asking || busy"
                @click="chooseWritePath('customize-first')"
              >
                <span class="article-chat__choice-title">
                  <span aria-hidden="true">☰</span>
                  {{ analyze ? $t('articleChat.customizeBeforeImprove') : $t('articleChat.customizeFirst') }}
                </span>
                <span class="article-chat__choice-line">{{ analyze ? $t('articleChat.customizeBeforeImproveLine') : $t('articleChat.customizeFirstLine') }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="article-chat__bottom">
        <template v-if="!resumeOffer && mode !== 'summary'">
          <p
            v-if="mode === 'locale' || mode === 'keyword'"
            class="article-chat__locale"
            data-test="article-chat-locale"
          >
            <span>{{ $t('articleChat.localeRow', [countryName, languageName]) }}</span>
            <button
              type="button"
              class="article-chat__text-button"
              data-test="article-chat-locale-change"
              @click="openLocalePicker"
            >
              {{ $t('articleChat.change') }}
            </button>
          </p>
          <template v-if="mode === 'topic'">
            <p class="article-chat__chips-label">
              {{ $t('articleChat.chipsLabel') }}
            </p>
            <div class="article-chat__chips">
              <button
                v-for="id in chipIds"
                :key="id"
                type="button"
                class="article-chat__chip"
                :aria-pressed="chipPressed(id) ? 'true' : 'false'"
                :data-test="'article-chat-chip-' + id"
                @click="appendChip(id)"
              >
                {{ $t('articleChat.chips.' + id) }}
              </button>
            </div>
          </template>
          <div
            v-else-if="mode === 'keyword'"
            data-test="article-chat-suggestions"
          >
            <p
              v-if="suggestionsLoading"
              class="article-chat__chips-label"
              data-test="article-chat-suggestions-loading"
            >
              {{ $t('articleChat.suggestionsLoading') }}
            </p>
            <div
              v-if="suggestionsLoading"
              class="article-chat__chips"
              aria-hidden="true"
            >
              <span
                v-for="n in 4"
                :key="'suggest-wait-' + n"
                class="article-chat__suggest article-chat__suggest--wait"
              />
            </div>
            <div
              v-show="!suggestionsLoading && suggestions.length"
              id="article-chat-suggestion-list"
              class="article-chat__chips"
              role="listbox"
              :aria-label="$t('articleChat.suggestionsLabel')"
            >
              <button
                v-for="(item, index) in suggestions"
                :id="'article-chat-suggestion-' + index"
                :key="item.keyword + '-' + item.monthly_searches_avg"
                type="button"
                role="option"
                class="article-chat__suggest"
                :class="{ 'article-chat__suggest--on': item.keyword === keyword }"
                :aria-selected="item.keyword === keyword ? 'true' : 'false'"
                :aria-pressed="item.keyword === keyword ? 'true' : 'false'"
                :data-test="'article-chat-suggestion'"
                @click="$emit('keyword', item.keyword)"
              >
                <span class="article-chat__suggest-phrase">
                  <span
                    v-if="item.keyword === keyword"
                    aria-hidden="true"
                  >✓ </span>{{ item.keyword }}
                </span>
                <span class="article-chat__suggest-volume">
                  <span class="article-chat__sr">{{ $t('keywords.avgMonthlySearches') }}</span>
                  {{ item.monthly_searches_avg }}
                </span>
              </button>
            </div>
          </div>
          <template v-else-if="mode === 'style'">
            <p class="article-chat__chips-label">
              {{ $t('createBrief.toneLabel') }}
            </p>
            <div
              class="article-chat__chips"
              role="group"
              :aria-label="$t('createBrief.toneLabel')"
            >
              <button
                v-for="id in toneIds"
                :key="'tone-' + id"
                type="button"
                class="article-chat__chip"
                :class="{ 'article-chat__chip--on': tone === id }"
                :aria-pressed="tone === id ? 'true' : 'false'"
                :data-test="'article-chat-tone-' + id"
                @click="$emit('tone', id)"
              >
                <span v-if="tone === id" aria-hidden="true">✓ </span>{{ $t('createBrief.tones.' + id) }}
              </button>
            </div>
            <p class="article-chat__chips-label">
              {{ $t('createBrief.audienceLabel') }}
            </p>
            <div
              class="article-chat__chips"
              role="group"
              :aria-label="$t('createBrief.audienceLabel')"
            >
              <button
                v-for="id in audienceIds"
                :key="'audience-' + id"
                type="button"
                class="article-chat__chip"
                :class="{ 'article-chat__chip--on': audience === id }"
                :aria-pressed="audience === id ? 'true' : 'false'"
                :data-test="'article-chat-audience-' + id"
                @click="$emit('audience', id)"
              >
                <span v-if="audience === id" aria-hidden="true">✓ </span>{{ $t('createBrief.audiences.' + id) }}
              </button>
            </div>
            <p class="article-chat__chips-label">
              {{ $t('createBrief.depthLabel') }}
            </p>
            <div
              class="article-chat__chips"
              role="group"
              :aria-label="$t('createBrief.depthLabel')"
            >
              <button
                v-for="id in depthIds"
                :key="'depth-' + id"
                type="button"
                class="article-chat__chip"
                :class="{ 'article-chat__chip--on': depth === id }"
                :aria-pressed="depth === id ? 'true' : 'false'"
                :data-test="'article-chat-depth-' + id"
                @click="$emit('depth', id)"
              >
                <span v-if="depth === id" aria-hidden="true">✓ </span>{{ $t('createBrief.depths.' + id) }}
              </button>
            </div>
            <div
              v-if="secondaryKeywords.length"
              class="article-chat__chips"
            >
              <span
                v-for="(word, index) in secondaryKeywords"
                :key="word + index"
                class="article-chat__secondary"
              >
                {{ word }}
                <button
                  type="button"
                  class="article-chat__secondary-remove"
                  :aria-label="$t('articleChat.removeSecondary', [word])"
                  :data-test="'article-chat-secondary-remove-' + index"
                  @click="$emit('secondary-remove', index)"
                >
                  ×
                </button>
              </span>
            </div>
          </template>
          <button
            type="button"
            ref="continueButton"
            class="article-chat__continue article-chat__continue--accent"
            data-test="article-chat-continue"
            :disabled="!canContinue"
            @click="tryContinue"
          >
            {{ $t('continue') }}
          </button>
          <p class="article-chat__hint">
            {{ continueHint }}
          </p>
        </template>
      </div>

      <div
        v-if="panelOpen"
        class="article-chat__panel"
        role="dialog"
        aria-modal="true"
        data-test="article-chat-panel"
        :aria-label="$t('articleChat.enteredTitle')"
      >
        <div class="article-chat__panel-card">
          <div class="article-chat__panel-bar">
            <h2 class="article-chat__panel-title">
              {{ $t('articleChat.enteredTitle') }}
            </h2>
            <button
              type="button"
              class="article-chat__text-button"
              data-test="article-chat-panel-close"
              @click="panelOpen = false"
            >
              {{ $t('close') }}
            </button>
          </div>
          <ul class="article-chat__summary">
            <li
              v-for="row in panelRows"
              :key="'panel-' + row.id"
              class="article-chat__history-row"
            >
              <span>✓ {{ row.step }} {{ row.title }} · {{ row.value }}</span>
              <button
                type="button"
                class="article-chat__text-button"
                :data-test="'article-chat-panel-change-' + row.id"
                @click="changeStep(row.id)"
              >
                {{ $t('articleChat.change') }}
              </button>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <header class="article-chat__bar">
      <nav
        class="article-chat__crumb"
        :aria-label="$t('article.create')"
      >
        <button
          type="button"
          class="article-chat__crumb-link"
          data-test="article-chat-articles"
          @click="$emit('cancel')"
        >
          {{ $t('article.list') }}
        </button>
        <span aria-hidden="true">›</span>
        <span>{{ analyze ? $t('article.analyze.title') : $t('article.create') }}</span>
      </nav>
      <div class="article-chat__progress">
        <span class="article-chat__step">{{ stepLabel }}</span>
        <div
          class="article-chat__track"
          role="progressbar"
          aria-valuemin="1"
          :aria-valuemax="totalSteps"
          :aria-valuenow="ariaStep"
          :aria-valuetext="stepLabel"
        >
          <div
            class="article-chat__fill"
            :style="{ width: progressWidth }"
          />
        </div>
      </div>
      <button
        type="button"
        class="article-chat__cancel"
        data-test="article-chat-cancel"
        @click="$emit('cancel')"
      >
        {{ $t('cancel') }}
      </button>
    </header>

    <v-dialog
      v-model="localeDialog"
      max-width="560"
    >
      <v-card
        data-test="article-chat-locale-dialog"
        class="pa-6"
      >
        <v-card-title class="px-0 pt-0">
          {{ $t('articleChat.pickerTitle') }}
        </v-card-title>
        <v-autocomplete
          v-model="draftLocation"
          :items="locationItems"
          :label="$t('keywords.location')"
          outlined
          hide-details="auto"
          data-test="article-chat-location"
        />
        <v-autocomplete
          v-model="draftLanguage"
          class="mt-4"
          :items="languageItems"
          :label="$t('keywords.language')"
          outlined
          hide-details="auto"
          data-test="article-chat-language"
        />
        <v-card-actions class="px-0 pb-0">
          <v-btn
            text
            data-test="article-chat-locale-cancel"
            @click="localeDialog = false"
          >
            {{ $t('cancel') }}
          </v-btn>
          <v-spacer />
          <v-btn
            color="primary"
            depressed
            data-test="article-chat-locale-save"
            @click="confirmLocalePicker"
          >
            {{ $t('keywords.confirm') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </section>
</template>

<script lang="ts">
    import Vue, {PropType} from 'vue';
    import {
        AUDIENCE_IDS,
        DEPTH_IDS,
        GEO_TEMPLATE_IDS,
        TONE_IDS,
        normalizeGeoTemplate,
    } from '@/config/WritingBrief';
    import {
        ARTICLE_CHAT_ANALYZE_FLOW,
        ARTICLE_CHAT_FLOW,
        ARTICLE_CHAT_TOPIC_MAX_WORDS,
        DEFAULT_IMPROVE_GOAL,
        IMPROVE_GOAL_IDS,
        TOPIC_CHIP_IDS,
        appendTopicChip,
        articleUrlState,
        chatStepNumber,
        goalCanContinue,
        goalSectionsFromHeadings,
        keywordReady,
        normalizeImproveGoal,
        normalizeSectionUids,
        languageOptions,
        localeChoiceLabel,
        locationOptions,
        normalizeLocale,
        foldHistoryWhenRaised,
        presentHistory,
        topicFieldState,
        topicPreview,
        topicWordCount,
        viewportRaisesChat,
        type ArticleChatSuggestion,
        type HistoryPresentation,
        type ArticleChatStepId,
        type HistoryRow,
        type GoalSection,
        type ImproveGoalId,
        type LoadedHeading,
        type TopicChipId,
    } from '@/service/articleChatSetup';

    export default Vue.extend({
        name: 'ArticleChatShell',
        props: {
            mode: {
                type: String,
                default: 'topic',
            },
            value: {
                type: String,
                default: '',
            },
            asking: {
                type: Boolean,
                default: false,
            },
            resumeOffer: {
                type: Boolean,
                default: false,
            },
            locationCode: {
                type: String,
                default: '2203',
            },
            languageCode: {
                type: String,
                default: 'cs',
            },
            historyRows: {
                type: Array as PropType<HistoryRow[]>,
                default: () => [],
            },
            enteredRows: {
                type: Array as PropType<HistoryRow[]>,
                default: () => [],
            },
            keyword: {
                type: String,
                default: '',
            },
            suggestions: {
                type: Array as PropType<ArticleChatSuggestion[]>,
                default: () => [],
            },
            suggestionsLoading: {
                type: Boolean,
                default: false,
            },
            geoTemplate: {
                type: String,
                default: 'universal',
            },
            tone: {
                type: String,
                default: 'professional',
            },
            audience: {
                type: String,
                default: 'general',
            },
            depth: {
                type: String,
                default: 'standard',
            },
            secondaryKeywords: {
                type: Array as PropType<string[]>,
                default: () => [],
            },
            secondaryDraft: {
                type: String,
                default: '',
            },
            busy: {
                type: Boolean,
                default: false,
            },
            flow: {
                type: String,
                default: 'create',
            },
            url: {
                type: String,
                default: '',
            },
            loadedTitle: {
                type: String,
                default: '',
            },
            loadedHeadings: {
                type: Array as PropType<LoadedHeading[]>,
                default: () => [],
            },
            reading: {
                type: Boolean,
                default: false,
            },
            improveGoal: {
                type: String,
                default: DEFAULT_IMPROVE_GOAL,
            },
            sectionUids: {
                type: Array as PropType<string[]>,
                default: () => [],
            },
        },
        data: function () {
            return {
                raised: false,
                viewportOffset: 0,
                viewportHeight: 0,
                activeSuggestionId: '',
                panelReturn: null as HTMLElement | null,
                layoutObserver: null as ResizeObserver | null,
                chipIds: TOPIC_CHIP_IDS,
                templateIds: GEO_TEMPLATE_IDS,
                toneIds: TONE_IDS,
                audienceIds: AUDIENCE_IDS,
                depthIds: DEPTH_IDS,
                maxWords: ARTICLE_CHAT_TOPIC_MAX_WORDS,
                panelOpen: false,
                localeDialog: false,
                localeReturn: null as HTMLElement | null,
                templateFocusId: '',
                templateEnterReady: false,
                goalFocusId: '',
                goalEnterReady: false,
                goalIds: IMPROVE_GOAL_IDS,
                draftLocation: '2203',
                draftLanguage: 'cs',
            };
        },
        computed: {
            fieldState: function (): string {
                return topicFieldState(this.value);
            },
            wordCount: function (): number {
                return topicWordCount(this.value);
            },
            keywordReady: function (): boolean {
                return keywordReady(this.keyword);
            },
            analyze: function (): boolean {
                return this.flow === 'analyze';
            },
            activeFlow: function (): ArticleChatStepId[] {
                return this.analyze ? ARTICLE_CHAT_ANALYZE_FLOW : ARTICLE_CHAT_FLOW;
            },
            totalSteps: function (): number {
                return this.activeFlow.length;
            },
            urlState: function (): string {
                return articleUrlState(this.url);
            },
            urlErrorText: function (): string {
                if (this.urlState === 'protocol') {
                    return String(this.$t('errors.startsWithProtocol'));
                }
                return String(this.$t('errors.invalidUrl'));
            },
            urlPreviewText: function (): string {
                return topicPreview(this.url) || '—';
            },
            canContinue: function (): boolean {
                if (this.asking || this.resumeOffer || this.busy || this.reading) {
                    return false;
                }
                if (this.mode === 'url') {
                    return this.urlState === 'ready';
                }
                if (this.mode === 'topic') {
                    return this.fieldState === 'ready';
                }
                if (this.mode === 'keyword') {
                    return this.keywordReady;
                }
                if (this.mode === 'goal') {
                    return goalCanContinue(this.improveGoal, this.sectionUids, this.loadedHeadings);
                }
                return this.mode === 'locale' || this.mode === 'loaded' || this.mode === 'template' || this.mode === 'style';
            },
            ariaStep: function (): number {
                if (this.mode === 'summary') {
                    return this.totalSteps;
                }
                if (
                    this.mode === 'url'
                    || this.mode === 'loaded'
                    || this.mode === 'topic'
                    || this.mode === 'locale'
                    || this.mode === 'keyword'
                    || this.mode === 'template'
                    || this.mode === 'style'
                    || this.mode === 'goal'
                ) {
                    return chatStepNumber(this.mode, this.activeFlow);
                }
                return 1;
            },
            stepLabel: function (): string {
                if (this.mode === 'summary') {
                    return String(this.$t('articleChat.summaryStep'));
                }
                return String(this.$t('articleChat.stepOf', [this.ariaStep, this.totalSteps]));
            },
            progressWidth: function (): string {
                return `${(this.ariaStep / this.totalSteps) * 100}%`;
            },
            names: function (): {country: string; language: string} {
                const locale = this.$i18n && this.$i18n.locale ? String(this.$i18n.locale) : 'cs';
                return localeChoiceLabel(this.locationCode, this.languageCode, locale);
            },
            countryName: function (): string {
                return this.names.country;
            },
            languageName: function (): string {
                return this.names.language;
            },
            topicPreviewText: function (): string {
                return topicPreview(this.value);
            },
            frameLabel: function (): string {
                if (this.mode === 'summary') {
                    return String(this.$t(this.analyze ? 'articleChat.summaryFrameAnalyze' : 'articleChat.summaryFrameLabel'));
                }
                if (this.mode === 'loaded') {
                    return String(this.$t('articleChat.loadedFrame'));
                }
                if (this.mode === 'template') {
                    return String(this.$t('articleChat.templateLabel'));
                }
                if (this.mode === 'goal') {
                    return String(this.$t('articleChat.goalFrame'));
                }
                return String(this.$t('articleChat.localeLabel'));
            },
            continueHint: function (): string {
                if (this.mode === 'url') {
                    return String(this.$t('articleChat.urlKeyHint'));
                }
                if (this.mode === 'loaded') {
                    return String(this.$t('articleChat.loadedKeyHint'));
                }
                if (this.mode === 'template') {
                    return String(this.$t('articleChat.templateKeyHint'));
                }
                if (this.mode === 'locale') {
                    return String(this.$t('articleChat.localeKeyHint'));
                }
                if (this.mode === 'keyword') {
                    return String(this.$t('articleChat.keywordKeyHint'));
                }
                if (this.mode === 'style') {
                    return String(this.$t('articleChat.styleKeyHint'));
                }
                if (this.mode === 'goal') {
                    return String(this.$t('articleChat.goalKeyHint'));
                }
                return String(this.$t('articleChat.keyHint'));
            },
            keywordPreviewText: function (): string {
                return topicPreview(this.keyword) || '—';
            },
            loadedTitleText: function (): string {
                const title = String(this.loadedTitle || '').replace(/\s+/g, ' ').trim();
                return title || String(this.$t('articleChat.loadedTitleMissing'));
            },
            loadedPreviewText: function (): string {
                const title = String(this.loadedTitle || '').replace(/\s+/g, ' ').trim();
                if (title) {
                    return topicPreview(title);
                }
                const first = this.loadedHeadings[0];
                return first ? topicPreview(first.text) : '—';
            },
            templateTitle: function (): string {
                return String(this.$t('createBrief.templates.' + normalizeGeoTemplate(this.geoTemplate) + '.title'));
            },
            stylePreview: function (): string {
                return [
                    String(this.$t('createBrief.tones.' + this.tone)),
                    String(this.$t('createBrief.audiences.' + this.audience)),
                    String(this.$t('createBrief.depths.' + this.depth)),
                ].join(' · ');
            },
            secondaryPreview: function (): string {
                return this.secondaryKeywords.length ? this.secondaryKeywords.join(', ') : '—';
            },
            goalSectionList: function (): GoalSection[] {
                return goalSectionsFromHeadings(this.loadedHeadings);
            },
            goalReady: function (): boolean {
                return goalCanContinue(this.improveGoal, this.sectionUids, this.loadedHeadings);
            },
            goalListOpen: function (): boolean {
                return this.mode === 'goal' && this.improveGoal === 'rewrite' && this.goalSectionList.length > 0;
            },
            goalPreviewText: function (): string {
                const goal = normalizeImproveGoal(this.improveGoal);
                const title = String(this.$t('articleChat.goals.' + goal + '.title'));
                if (goal !== 'rewrite') {
                    return title;
                }
                const names = this.goalSectionList
                    .filter((section) => this.sectionOn(section.id))
                    .map((section) => section.text);
                if (!names.length) {
                    return title;
                }
                return topicPreview(`${title} · ${names.join(', ')}`);
            },
            historyView: function (): HistoryPresentation {
                return foldHistoryWhenRaised(presentHistory(this.historyRows), this.raised);
            },
            raisedStyle: function (): Record<string, string> {
                if (!this.raised) {
                    return {};
                }
                return {
                    '--chat-vv-offset': `${this.viewportOffset}px`,
                    '--chat-vv-height': `${this.viewportHeight}px`,
                };
            },
            suggestionListOpen: function (): boolean {
                return this.mode === 'keyword' && !this.suggestionsLoading && this.suggestions.length > 0;
            },
            urlDescribedBy: function (): string {
                if (this.urlState === 'empty') {
                    return 'article-chat-url-empty article-chat-url-example';
                }
                if (this.urlState !== 'ready') {
                    return 'article-chat-url-invalid article-chat-url-example';
                }
                return 'article-chat-url-example';
            },
            showHistory: function (): boolean {
                const first = this.activeFlow[0];
                return this.mode !== 'summary'
                    && this.mode !== first
                    && !this.resumeOffer
                    && (this.historyView.folded || this.historyView.visible.length > 0);
            },
            panelRows: function (): HistoryRow[] {
                return this.enteredRows.length ? this.enteredRows : this.historyView.panel;
            },
            uiLang: function (): string {
                return this.$i18n && this.$i18n.locale ? String(this.$i18n.locale) : 'cs';
            },
            locationItems: function (): {value: string; text: string}[] {
                return locationOptions(this.uiLang);
            },
            languageItems: function (): {value: string; text: string}[] {
                return languageOptions(this.draftLocation, this.uiLang);
            },
        },
        watch: {
            asking: function (): void {
                this.focusCurrent();
            },
            resumeOffer: function (): void {
                this.focusCurrent();
            },
            mode: function (): void {
                this.panelOpen = false;
                this.localeDialog = false;
                this.activeSuggestionId = '';
                this.templateFocusId = '';
                this.templateEnterReady = false;
                this.goalFocusId = '';
                this.goalEnterReady = false;
                this.focusCurrent();
            },
            localeDialog: function (open: boolean): void {
                if (open) {
                    return;
                }
                const back = this.localeReturn;
                this.localeReturn = null;
                this.$nextTick(() => {
                    const card = this.$refs.localeCard as HTMLElement | undefined;
                    const target = back && typeof back.focus === 'function' ? back : card;
                    if (target && typeof target.focus === 'function') {
                        target.focus();
                    }
                });
            },
            panelOpen: function (open: boolean): void {
                if (open) {
                    const active = document.activeElement as HTMLElement | null;
                    this.panelReturn = active && active !== document.body ? active : null;
                    this.$nextTick(() => {
                        const closeBtn = this.$el.querySelector('[data-test="article-chat-panel-close"]') as HTMLElement | null;
                        if (closeBtn) {
                            closeBtn.focus();
                        }
                    });
                    return;
                }
                const back = this.panelReturn;
                this.panelReturn = null;
                if (back && typeof back.focus === 'function') {
                    this.$nextTick(() => back.focus());
                }
            },
            draftLocation: function (): void {
                const next = normalizeLocale(this.draftLocation, this.draftLanguage);
                if (next.languageCode !== this.draftLanguage) {
                    this.draftLanguage = next.languageCode;
                }
            },
        },
        mounted: function (): void {
            this.syncRaised();
            window.addEventListener('resize', this.syncRaised);
            window.addEventListener('keydown', this.onWindowKey);
            if (window.visualViewport) {
                window.visualViewport.addEventListener('resize', this.syncRaised);
                window.visualViewport.addEventListener('scroll', this.syncRaised);
            }
            if (typeof ResizeObserver !== 'undefined') {
                this.layoutObserver = new ResizeObserver(this.syncRaised);
                this.layoutObserver.observe(document.body);
            }
            this.focusCurrent();
        },
        beforeDestroy: function (): void {
            window.removeEventListener('resize', this.syncRaised);
            window.removeEventListener('keydown', this.onWindowKey);
            if (window.visualViewport) {
                window.visualViewport.removeEventListener('resize', this.syncRaised);
                window.visualViewport.removeEventListener('scroll', this.syncRaised);
            }
            if (this.layoutObserver) {
                this.layoutObserver.disconnect();
                this.layoutObserver = null;
            }
        },
        methods: {
            onUrl: function (event: Event): void {
                const target = event.target as HTMLInputElement | null;
                this.$emit('url', target ? target.value : '');
            },
            onInput: function (event: Event): void {
                const target = event.target as HTMLTextAreaElement | null;
                this.$emit('input', target ? target.value : '');
            },
            onKeyword: function (event: Event): void {
                const target = event.target as HTMLInputElement | null;
                this.$emit('keyword', target ? target.value : '');
            },
            onSecondaryDraft: function (event: Event): void {
                const target = event.target as HTMLInputElement | null;
                this.$emit('secondary-draft', target ? target.value : '');
            },
            onFocusIn: function (event: FocusEvent): void {
                const target = event.target as HTMLElement | null;
                if (!target || typeof target.closest !== 'function' || !target.closest('.article-chat__chips')) {
                    return;
                }
                if (typeof target.scrollIntoView === 'function') {
                    target.scrollIntoView({block: 'nearest', inline: 'nearest'});
                }
            },
            onKeydown: function (event: KeyboardEvent): void {
                if (this.panelOpen && event.key === 'Tab') {
                    this.trapPanelFocus(event);
                    return;
                }
                if (this.localeDialog || this.panelOpen) {
                    return;
                }
                if (this.mode === 'template' && this.moveTemplate(event)) {
                    return;
                }
                if (this.mode === 'goal' && this.moveGoal(event)) {
                    return;
                }
                if (this.mode === 'template' && event.key === 'Enter' && !event.shiftKey && this.activateTemplateCard(event)) {
                    return;
                }
                if (this.mode === 'goal' && event.key === 'Enter' && !event.shiftKey && this.activateGoalCard(event)) {
                    return;
                }
                if (this.mode === 'locale' && event.key === 'Enter' && !event.shiftKey && this.activateLocaleCard(event)) {
                    return;
                }
                if (this.mode === 'keyword' && this.moveSuggestion(event)) {
                    return;
                }
                if (event.key !== 'Enter' || event.shiftKey) {
                    return;
                }
                const target = event.target as HTMLElement | null;
                if (target && target.tagName === 'BUTTON') {
                    return;
                }
                if (this.mode === 'style' && target && target.getAttribute('data-test') === 'article-chat-secondary') {
                    event.preventDefault();
                    this.$emit('secondary-add');
                    return;
                }
                if (this.mode === 'goal' && target && (target.tagName === 'INPUT' || target.tagName === 'LABEL')) {
                    return;
                }
                if (this.mode === 'url' || this.mode === 'loaded' || this.mode === 'topic' || this.mode === 'locale' || this.mode === 'keyword' || this.mode === 'template' || this.mode === 'style' || this.mode === 'goal') {
                    event.preventDefault();
                    this.tryContinue();
                }
            },
            moveTemplate: function (event: KeyboardEvent): boolean {
                if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp' && event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
                    return false;
                }
                const target = event.target as HTMLElement | null;
                if (!target || typeof target.closest !== 'function' || !target.closest('.article-chat__cards')) {
                    return false;
                }
                const ids = this.templateIds as string[];
                const current = this.focusedTemplateId();
                let index = ids.indexOf(current);
                if (index < 0) {
                    index = 0;
                }
                const backward = event.key === 'ArrowUp' || event.key === 'ArrowLeft';
                const next = backward ? (index - 1 + ids.length) % ids.length : (index + 1) % ids.length;
                event.preventDefault();
                this.templateFocusId = ids[next];
                this.templateEnterReady = false;
                this.$nextTick(() => {
                    const card = this.$el.querySelector(
                        '[data-test="article-chat-template-' + ids[next] + '"]',
                    ) as HTMLElement | null;
                    if (card) {
                        card.focus();
                    }
                });
                return true;
            },
            focusedTemplateId: function (): string {
                const ids = this.templateIds as string[];
                if (this.templateFocusId && ids.indexOf(this.templateFocusId) !== -1) {
                    return this.templateFocusId;
                }
                const selected = normalizeGeoTemplate(this.geoTemplate);
                if (ids.indexOf(selected) !== -1) {
                    return selected;
                }
                return ids[0] || 'universal';
            },
            templateTabIndex: function (id: string): number {
                return id === this.focusedTemplateId() ? 0 : -1;
            },
            selectTemplate: function (id: string): void {
                this.templateFocusId = id;
                this.templateEnterReady = true;
                this.$emit('template', id);
            },
            moveGoal: function (event: KeyboardEvent): boolean {
                if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp' && event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
                    return false;
                }
                const target = event.target as HTMLElement | null;
                if (!target || typeof target.closest !== 'function' || !target.closest('[role="radiogroup"]')) {
                    return false;
                }
                const ids: readonly string[] = this.goalIds;
                const current = this.focusedGoalId();
                let index = ids.indexOf(current);
                if (index < 0) {
                    index = 0;
                }
                const backward = event.key === 'ArrowUp' || event.key === 'ArrowLeft';
                const next = backward ? (index - 1 + ids.length) % ids.length : (index + 1) % ids.length;
                event.preventDefault();
                this.goalFocusId = ids[next];
                this.goalEnterReady = false;
                this.$nextTick(() => {
                    const card = this.$el.querySelector(
                        '[data-test="article-chat-goal-' + ids[next] + '"]',
                    ) as HTMLElement | null;
                    if (card) {
                        card.focus();
                    }
                });
                return true;
            },
            focusedGoalId: function (): string {
                const ids: readonly string[] = this.goalIds;
                if (this.goalFocusId && ids.indexOf(this.goalFocusId) !== -1) {
                    return this.goalFocusId;
                }
                const selected = normalizeImproveGoal(this.improveGoal);
                if (ids.indexOf(selected) !== -1) {
                    return selected;
                }
                return ids[0] || DEFAULT_IMPROVE_GOAL;
            },
            goalTabIndex: function (id: string): number {
                return id === this.focusedGoalId() ? 0 : -1;
            },
            selectGoal: function (id: string): void {
                const goal = normalizeImproveGoal(id) as ImproveGoalId;
                this.goalFocusId = goal;
                this.goalEnterReady = true;
                this.$emit('goal', goal);
            },
            activateGoalCard: function (event: KeyboardEvent): boolean {
                const target = event.target as HTMLElement | null;
                const card = target && typeof target.closest === 'function'
                    ? target.closest('[role="radio"]') as HTMLElement | null
                    : null;
                if (!card || typeof card.closest !== 'function' || !card.closest('[role="radiogroup"]')) {
                    return false;
                }
                const testId = card.getAttribute('data-test') || '';
                const prefix = 'article-chat-goal-';
                if (testId.indexOf(prefix) !== 0) {
                    return false;
                }
                const id = testId.slice(prefix.length);
                event.preventDefault();
                this.goalFocusId = id;
                if (!this.goalEnterReady) {
                    this.goalEnterReady = true;
                    if (normalizeImproveGoal(this.improveGoal) !== id) {
                        this.$emit('goal', normalizeImproveGoal(id));
                    }
                    return true;
                }
                this.tryContinue();
                return true;
            },
            sectionOn: function (id: string): boolean {
                return normalizeSectionUids(this.sectionUids, this.goalSectionList).indexOf(id) !== -1;
            },
            toggleSection: function (id: string): void {
                const current = normalizeSectionUids(this.sectionUids, this.goalSectionList);
                const next = current.indexOf(id) === -1
                    ? current.concat([id])
                    : current.filter((item) => item !== id);
                this.$emit('sections', next);
            },
            activateLocaleCard: function (event: KeyboardEvent): boolean {
                const target = event.target as HTMLElement | null;
                const card = target && typeof target.closest === 'function'
                    ? target.closest('[data-test="article-chat-locale-card"]')
                    : null;
                if (!card) {
                    return false;
                }
                event.preventDefault();
                this.openLocalePicker(event);
                return true;
            },
            activateTemplateCard: function (event: KeyboardEvent): boolean {
                const target = event.target as HTMLElement | null;
                const card = target && typeof target.closest === 'function'
                    ? target.closest('[role="radio"]') as HTMLElement | null
                    : null;
                if (!card || typeof card.closest !== 'function' || !card.closest('.article-chat__cards')) {
                    return false;
                }
                const testId = card.getAttribute('data-test') || '';
                const prefix = 'article-chat-template-';
                if (testId.indexOf(prefix) !== 0) {
                    return false;
                }
                const id = testId.slice(prefix.length);
                event.preventDefault();
                this.templateFocusId = id;
                if (!this.templateEnterReady) {
                    this.templateEnterReady = true;
                    if (normalizeGeoTemplate(this.geoTemplate) !== id) {
                        this.$emit('template', id);
                    }
                    return true;
                }
                this.tryContinue();
                return true;
            },
            moveSuggestion: function (event: KeyboardEvent): boolean {
                if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') {
                    return false;
                }
                const target = event.target as HTMLElement | null;
                if (!target || typeof target.getAttribute !== 'function') {
                    return false;
                }
                const inField = target.getAttribute('data-test') === 'article-chat-keyword';
                const inList = target.getAttribute('data-test') === 'article-chat-suggestion';
                if (!inField && !inList) {
                    return false;
                }
                const options = Array.prototype.slice.call(
                    this.$el.querySelectorAll('[data-test="article-chat-suggestion"]'),
                ) as HTMLElement[];
                if (!options.length) {
                    return false;
                }
                const index = options.indexOf(target);
                let next = 0;
                if (event.key === 'ArrowDown') {
                    next = index < 0 ? 0 : Math.min(options.length - 1, index + 1);
                } else if (index < 0) {
                    next = options.length - 1;
                } else {
                    next = Math.max(0, index - 1);
                }
                event.preventDefault();
                this.activeSuggestionId = options[next].id || '';
                options[next].focus();
                return true;
            },
            trapPanelFocus: function (event: KeyboardEvent): void {
                const root = this.$el.querySelector('[data-test="article-chat-panel"]');
                if (!root) {
                    return;
                }
                const items = Array.prototype.slice.call(
                    root.querySelectorAll('button, [href], input, textarea, select, [tabindex]:not([tabindex="-1"])'),
                ) as HTMLElement[];
                if (!items.length) {
                    return;
                }
                const first = items[0];
                const last = items[items.length - 1];
                const active = document.activeElement as HTMLElement | null;
                const inside = !!active && root.contains(active);
                if (event.shiftKey && (!inside || active === first)) {
                    event.preventDefault();
                    last.focus();
                    return;
                }
                if (!event.shiftKey && (!inside || active === last)) {
                    event.preventDefault();
                    first.focus();
                }
            },
            chipPressed: function (id: TopicChipId): boolean {
                const phrase = String(this.$t('articleChat.chipExamples.' + id));
                return phrase.length > 0 && this.value.indexOf(phrase) !== -1;
            },
            onWindowKey: function (event: KeyboardEvent): void {
                if (event.key !== 'Escape') {
                    return;
                }
                if (this.localeDialog) {
                    this.localeDialog = false;
                    return;
                }
                if (this.panelOpen) {
                    this.panelOpen = false;
                    return;
                }
                if (typeof document !== 'undefined' && document.querySelector('.v-dialog--active')) {
                    return;
                }
                event.preventDefault();
                this.focusCurrent();
            },
            appendChip: function (id: TopicChipId): void {
                const phrase = String(this.$t('articleChat.chipExamples.' + id));
                this.$emit('input', appendTopicChip(this.value, phrase));
            },
            tryContinue: function (): void {
                if (!this.canContinue) {
                    return;
                }
                this.$emit('continue');
            },
            chooseWritePath: function (path: 'immediate' | 'customize-first'): void {
                if (this.asking || this.busy || this.resumeOffer || this.mode !== 'summary') {
                    return;
                }
                this.$emit('choose', path);
            },
            changeStep: function (id: string): void {
                this.panelOpen = false;
                if (id === 'url' || id === 'loaded' || id === 'topic' || id === 'locale' || id === 'keyword' || id === 'template' || id === 'style' || id === 'goal') {
                    this.$emit('change', id);
                }
            },
            openLocalePicker: function (event?: Event): void {
                const openerSelector = '[data-test="article-chat-locale-card"], [data-test="article-chat-locale-change"]';
                const current = event && event.currentTarget instanceof HTMLElement
                    ? event.currentTarget
                    : null;
                const hit = event && event.target instanceof HTMLElement ? event.target : null;
                const fromCurrent = current && typeof current.closest === 'function'
                    ? current.closest(openerSelector) as HTMLElement | null
                    : null;
                const fromHit = hit && typeof hit.closest === 'function'
                    ? hit.closest(openerSelector) as HTMLElement | null
                    : null;
                const active = document.activeElement as HTMLElement | null;
                this.localeReturn = fromCurrent || fromHit || (active && typeof active.focus === 'function' ? active : null);
                const next = normalizeLocale(this.locationCode, this.languageCode);
                this.draftLocation = next.locationCode;
                this.draftLanguage = next.languageCode;
                this.localeDialog = true;
            },
            confirmLocalePicker: function (): void {
                const next = normalizeLocale(this.draftLocation, this.draftLanguage);
                this.draftLocation = next.locationCode;
                this.draftLanguage = next.languageCode;
                this.localeDialog = false;
                this.$emit('locale', next);
            },
            focusCurrent: function (): void {
                this.$nextTick(() => {
                    if (this.asking || this.resumeOffer) {
                        return;
                    }
                    const prompt = this.$refs.prompt as HTMLTextAreaElement | undefined;
                    const urlInput = this.$refs.urlInput as HTMLInputElement | undefined;
                    const keyword = this.$refs.keyword as HTMLInputElement | undefined;
                    const secondary = this.$refs.secondary as HTMLInputElement | undefined;
                    const card = this.$refs.localeCard as HTMLButtonElement | undefined;
                    const templateCard = this.$el.querySelector(
                        '[data-test="article-chat-template-' + normalizeGeoTemplate(this.geoTemplate) + '"]',
                    ) as HTMLButtonElement | null;
                    const choice = this.$refs.choiceImmediate as HTMLButtonElement | undefined;
                    const continueButton = this.$refs.continueButton as HTMLButtonElement | undefined;
                    if (this.mode === 'url' && urlInput) {
                        urlInput.focus();
                    } else if (this.mode === 'topic' && prompt) {
                        prompt.focus();
                    } else if (this.mode === 'keyword' && keyword) {
                        keyword.focus();
                    } else if (this.mode === 'style' && secondary) {
                        secondary.focus();
                    } else if (this.mode === 'locale' && card) {
                        card.focus();
                    } else if (this.mode === 'template' && templateCard) {
                        templateCard.focus();
                    } else if (this.mode === 'goal') {
                        const goalCard = this.$el.querySelector(
                            '[data-test="article-chat-goal-' + this.focusedGoalId() + '"]',
                        ) as HTMLButtonElement | null;
                        if (goalCard) {
                            goalCard.focus();
                        }
                    } else if (this.mode === 'loaded' && continueButton) {
                        continueButton.focus();
                    } else if (this.mode === 'summary' && choice) {
                        choice.focus();
                    }
                });
            },
            syncRaised: function (): void {
                const shortScreen = typeof window.matchMedia === 'function'
                    && window.matchMedia('(max-height: 560px)').matches;
                const viewport = window.visualViewport;
                const innerHeight = window.innerHeight || 0;
                const viewportHeight = viewport ? viewport.height : innerHeight;
                const offsetTop = viewport ? viewport.offsetTop : 0;
                const raised = viewportRaisesChat({
                    innerHeight,
                    viewportHeight,
                    maxHeightMatches: shortScreen,
                });
                const wasRaised = this.raised;
                this.raised = raised;
                if (!raised) {
                    this.viewportOffset = 0;
                    this.viewportHeight = 0;
                    return;
                }
                const appBar = 112;
                const visibleTop = offsetTop;
                this.viewportOffset = Math.max(0, Math.round(visibleTop > appBar ? visibleTop - appBar : 0));
                if (!wasRaised || this.viewportHeight === 0) {
                    this.viewportHeight = Math.max(220, Math.round(viewportHeight - appBar));
                }
                this.$nextTick(() => {
                    this.fitRaisedHeight();
                });
            },
            fitRaisedHeight: function (): void {
                if (!this.raised) {
                    return;
                }
                const el = this.$el as HTMLElement | undefined;
                if (!el || typeof el.getBoundingClientRect !== 'function') {
                    return;
                }
                const viewport = window.visualViewport;
                const viewHeight = viewport ? viewport.height : (window.innerHeight || 0);
                const top = el.getBoundingClientRect().top;
                const available = Math.max(220, Math.round(viewHeight - Math.max(top, 0) - 8));
                if (Math.abs(available - this.viewportHeight) > 2) {
                    this.viewportHeight = available;
                }
                const button = el.querySelector(
                    '[data-test="article-chat-continue"], [data-test="article-chat-generate-now"]',
                ) as HTMLElement | null;
                if (!button || typeof button.scrollIntoView !== 'function') {
                    return;
                }
                const buttonRect = button.getBoundingClientRect();
                if (buttonRect.bottom > viewHeight - 4 || buttonRect.top < 0) {
                    button.scrollIntoView({block: 'end', inline: 'nearest'});
                }
            },
        },
    });
</script>

<style scoped lang="sass">
.article-chat
    width: 100%
    max-width: 760px
    min-width: 0
    margin: 0 auto
    min-height: calc(100dvh - 112px)
    display: flex
    flex-direction: column
    color: var(--sw-text)

.article-chat__bar
    order: -1
    display: flex
    flex-wrap: wrap
    align-items: center
    gap: 12px 16px
    margin-bottom: 8px
    min-width: 0
    max-width: 100%

.article-chat__crumb
    display: flex
    flex-wrap: wrap
    align-items: center
    gap: 6px
    min-width: 0
    max-width: 100%
    font-size: 0.95rem

.article-chat__crumb-link,
.article-chat__cancel,
.article-chat__text-button
    min-width: 44px
    min-height: 44px
    padding: 8px 4px
    border: 0
    background: transparent
    color: var(--sw-primary-fill)
    font: inherit
    cursor: pointer

    &:focus
        outline: 2px solid var(--sw-primary)
        outline-offset: 2px

.article-chat__text-button
    text-decoration: underline

.article-chat__progress
    display: flex
    align-items: center
    gap: 10px
    flex: 1 1 140px
    min-width: 0
    max-width: 100%

.article-chat__step
    white-space: nowrap
    font-size: 0.95rem

.article-chat__track
    flex: 1
    height: 6px
    border-radius: var(--sw-radius-pill)
    background: var(--sw-neutral-surface-alt)
    overflow: hidden

.article-chat__fill
    height: 100%
    background: var(--sw-primary-fill)

.article-chat__stage
    position: relative
    flex: 1 1 auto
    display: grid
    grid-template-rows: minmax(0, 1fr) auto minmax(0, 1fr)
    min-height: 0
    min-width: 0
    max-width: 100%

.article-chat--raised
    min-height: 0
    margin-top: var(--chat-vv-offset, 0px)

    .article-chat__stage
        display: flex
        flex-direction: column
        grid-template-rows: none
        min-height: 0
        justify-content: flex-start

    .article-chat__top
        flex: 0 1 auto
        min-height: 0
        max-height: 30vh
        overflow: auto

    .article-chat__center
        flex: 0 0 auto
        overflow: visible

    .article-chat__bottom
        flex: 0 0 auto
        padding-bottom: 6px

    .article-chat__inner
        padding: 10px 12px 8px

    .article-chat__input
        min-height: 72px

    .article-chat__input--line
        min-height: 44px

    .article-chat__greeting
        margin-bottom: 4px

    .article-chat__outline,
    .article-chat__sections
        max-height: 88px

.article-chat__center
    min-height: 0
    min-width: 0
    max-width: 100%
    overflow: auto

.article-chat__top
    display: flex
    flex-direction: column
    justify-content: flex-end
    min-width: 0
    max-width: 100%
    overflow: auto
    padding-bottom: 12px

.article-chat__history,
.article-chat__summary
    list-style: none
    margin: 0
    padding: 0

.article-chat__history-row
    display: flex
    flex-wrap: wrap
    align-items: center
    gap: 8px 12px
    margin: 0

.article-chat__history-link
    align-self: flex-start

.article-chat__greeting
    margin: 0 0 8px
    font-size: 1.05rem
    line-height: 1.45

.article-chat__asking
    margin: 0
    min-height: 44px
    display: flex
    align-items: center

.article-chat__dots
    display: inline-flex
    gap: 6px

    span
        width: 8px
        height: 8px
        border-radius: 50%
        background: var(--sw-primary)
        animation: article-chat-blink 0.9s infinite

        &:nth-child(2)
            animation-delay: 0.15s

        &:nth-child(3)
            animation-delay: 0.3s

.article-chat__shell
    border-radius: var(--sw-radius-md)
    padding: 0
    background: var(--v-surface-base)
    border: 1px solid var(--sw-neutral-border)
    box-shadow: var(--sw-shadow-sm)

.article-chat__inner
    border-radius: inherit
    background: transparent
    padding: 16px 18px 12px

.article-chat__label
    display: block
    font-weight: 700
    margin-bottom: 8px

.article-chat__loaded-title
    margin: 0 0 8px
    overflow-wrap: anywhere

.article-chat__loaded-kicker
    display: block
    font-size: 0.85rem
    font-weight: 700
    margin-bottom: 4px

.article-chat__outline
    margin: 0 0 8px
    padding: 0
    max-height: 220px
    overflow: auto
    min-width: 0
    max-width: 100%

.article-chat__outline-item
    display: flex
    gap: 8px
    margin: 0
    padding: 2px 0
    overflow-wrap: anywhere
    line-height: 1.35
    list-style: none
    min-width: 0

    &[data-level="2"]
        padding-left: 12px

    &[data-level="3"]
        padding-left: 24px

    &[data-level="4"]
        padding-left: 36px

    &[data-level="5"]
        padding-left: 48px

    &[data-level="6"]
        padding-left: 60px

.article-chat__outline-level
    flex: 0 0 auto
    font-weight: 700
    color: var(--sw-primary-fill)

.article-chat__outline-text
    min-width: 0
    overflow-wrap: anywhere

.article-chat__loaded-stay
    margin: 8px 0 0
    overflow-wrap: anywhere
    line-height: 1.4

.article-chat__input
    width: 100%
    min-height: 120px
    border: 0
    resize: vertical
    font: inherit
    line-height: 1.45
    background: transparent
    color: inherit

    &:focus
        outline: 2px solid var(--sw-primary)
        outline-offset: 2px

.article-chat__input--line
    min-height: 44px
    resize: none

.article-chat__field-bar
    display: flex
    align-items: baseline
    justify-content: space-between
    gap: 12px

    .article-chat__label
        margin-bottom: 8px

.article-chat__secondary-row
    display: flex
    align-items: center
    gap: 8px

.article-chat__add
    min-height: 44px
    padding: 0 16px
    border-radius: var(--sw-radius-button)
    border: 1px solid var(--sw-primary)
    background: var(--v-surface-base)
    color: var(--sw-primary-fill)
    font: inherit
    cursor: pointer
    white-space: nowrap

    &:focus
        outline: 2px solid var(--sw-primary)
        outline-offset: 2px

.article-chat__cards
    display: grid
    grid-template-columns: 1fr
    gap: 8px

.article-chat__card-title
    display: block
    font-weight: 700

.article-chat__card-blurb
    display: block
    margin-top: 4px
    font-size: 0.9rem
    line-height: 1.4
    color: var(--sw-text-2)

.article-chat__suggest
    display: inline-flex
    align-items: stretch
    min-height: 44px
    padding: 0
    border-radius: var(--sw-radius-button)
    border: 1px solid var(--sw-neutral-border-strong)
    background: var(--v-surface-base)
    color: inherit
    font: inherit
    overflow: hidden
    cursor: pointer

    &:focus
        outline: 2px solid var(--sw-primary)
        outline-offset: 2px

.article-chat__suggest-phrase,
.article-chat__suggest-volume
    display: flex
    align-items: center
    padding: 8px 10px

.article-chat__suggest-volume
    border-left: 1px solid var(--sw-neutral-border)
    background: var(--sw-neutral-surface-soft)

.article-chat__suggest--on
    background: var(--sw-primary-fill)
    border-color: var(--sw-primary-fill)
    color: var(--sw-on-accent)

    .article-chat__suggest-volume
        border-left-color: rgba(255, 255, 255, 0.35)
        background: rgba(255, 255, 255, 0.16)

.article-chat__suggest--wait
    width: 148px
    background: var(--sw-neutral-surface-alt)
    pointer-events: none
    position: relative

    &::after
        content: ''
        position: absolute
        left: 8px
        right: 8px
        bottom: 8px
        height: 3px
        border-radius: var(--sw-radius-pill)
        background: var(--sw-primary)
        animation: article-chat-blink 0.9s infinite

.article-chat__secondary
    display: inline-flex
    align-items: center
    gap: 6px
    min-height: 44px
    padding: 6px 8px 6px 12px
    border-radius: var(--sw-radius-pill)
    background: var(--sw-reader-eat)
    color: var(--sw-success-text)
    border: 1px solid var(--sw-success-fill)

.article-chat__secondary-remove
    min-width: 44px
    min-height: 44px
    border: 0
    background: transparent
    color: inherit
    font: inherit
    cursor: pointer

    &:focus
        outline: 2px solid var(--sw-primary)
        outline-offset: 2px

.article-chat__card
    width: 100%
    min-height: 44px
    text-align: left
    border-radius: var(--sw-radius-md)
    border: 1px solid var(--sw-neutral-border)
    background: var(--v-surface-base)
    color: inherit
    font: inherit
    padding: 12px 14px
    cursor: pointer

    &:focus
        outline: 2px solid var(--sw-primary)
        outline-offset: 2px

.article-chat__card--selected
    background: var(--sw-primary-soft)
    border-color: var(--sw-primary)

.article-chat__goal-sections
    margin-top: 10px
    min-width: 0
    max-width: 100%

.article-chat__sections
    list-style: none
    margin: 0
    padding: 0
    max-height: 180px
    overflow: auto
    min-width: 0
    max-width: 100%

.article-chat__section
    display: flex
    align-items: flex-start
    gap: 10px
    min-height: 44px
    padding: 8px 2px
    cursor: pointer
    overflow-wrap: anywhere

    input
        width: 20px
        height: 20px
        margin-top: 2px
        flex: 0 0 auto

    &:focus-within
        outline: 2px solid var(--sw-primary)
        outline-offset: 2px

.article-chat__count
    text-align: right
    font-size: 0.85rem
    color: var(--sw-neutral-muted)

.article-chat__count--over,
.article-chat__error
    color: var(--v-error-base)

.article-chat__empty,
.article-chat__error,
.article-chat__hint,
.article-chat__chips-label
    margin: 8px 0 0
    font-size: 0.9rem
    line-height: 1.4

.article-chat__empty,
.article-chat__hint,
.article-chat__chips-label
    color: var(--sw-text-2)

.article-chat__bottom
    display: flex
    flex-direction: column
    align-items: stretch
    justify-content: flex-start
    min-width: 0
    max-width: 100%
    padding-top: 12px

.article-chat__locale
    display: flex
    flex-wrap: wrap
    align-items: center
    gap: 8px 12px
    margin: 0 0 8px

.article-chat__chips
    display: flex
    flex-wrap: wrap
    gap: 8px
    margin-top: 8px
    min-width: 0
    max-width: 100%

.article-chat__chip
    min-height: 44px
    padding: 8px 14px
    border-radius: var(--sw-radius-pill)
    border: 1px solid var(--sw-primary)
    background: var(--v-surface-base)
    color: var(--sw-primary-fill)
    font: inherit
    cursor: pointer

    &:focus
        outline: 2px solid var(--sw-primary)
        outline-offset: 2px

.article-chat__chip--on
    background: var(--sw-primary-soft)
    border-color: var(--sw-primary)
    color: var(--sw-primary-fill)

.article-chat__continue
    min-height: 44px
    margin-top: 16px
    align-self: flex-start
    border-radius: var(--sw-radius-button)
    padding: 0 22px
    font: inherit
    font-weight: 500
    letter-spacing: 0.02em
    cursor: pointer

    &:disabled
        cursor: default
        opacity: 0.45

    &:focus
        outline: 2px solid var(--sw-primary)
        outline-offset: 2px

.article-chat__continue--accent
    border: 0
    background: var(--sw-success-fill)
    color: var(--sw-on-accent)

.article-chat__continue--outline
    border: 1px solid var(--sw-primary)
    background: transparent
    color: var(--sw-primary-fill)

.article-chat__choices
    display: flex
    flex-direction: column
    gap: 12px

.article-chat__choice
    min-height: 44px
    width: 100%
    border-radius: var(--sw-radius-md)
    padding: 14px 16px
    font: inherit
    text-align: left
    cursor: pointer

    &:disabled
        cursor: default
        opacity: 0.45

    &:focus
        outline: 2px solid var(--sw-primary)
        outline-offset: 2px

.article-chat__choice--accent
    border: 0
    background: var(--sw-success-fill)
    color: var(--sw-on-accent)

.article-chat__choice--outline
    border: 1px solid var(--sw-primary)
    background: var(--v-surface-base)
    color: var(--sw-primary-fill)

.article-chat__choice-title
    display: block
    font-weight: 700

.article-chat__choice-line
    display: block
    margin-top: 4px
    font-size: 0.92rem
    font-weight: 400
    line-height: 1.4

.article-chat__resume-actions
    display: flex
    flex-wrap: wrap
    gap: 8px

.article-chat__panel
    position: absolute
    inset: 0
    z-index: 4
    display: flex
    justify-content: center
    align-items: flex-start
    overflow: auto
    padding: 12px
    background: rgba(255, 255, 255, 0.78)

.article-chat__panel-card
    width: 100%
    max-width: 640px
    border-radius: var(--sw-radius-md)
    background: var(--v-surface-base)
    border: 1px solid var(--sw-neutral-border)
    box-shadow: var(--sw-shadow-lg)
    padding: 16px 18px

.article-chat__panel-bar
    display: flex
    align-items: center
    justify-content: space-between
    gap: 12px

.article-chat__panel-title
    margin: 0
    font-size: 1.05rem

.article-chat__sr
    position: absolute
    width: 1px
    height: 1px
    padding: 0
    margin: -1px
    overflow: hidden
    clip: rect(0, 0, 0, 0)
    border: 0

.theme--dark
    .article-chat__panel
        background: rgba(0, 0, 0, 0.55)

@media (min-width: 700px)
    .article-chat__cards
        grid-template-columns: 1fr 1fr

@media (max-width: 600px)
    .article-chat
        overflow-x: clip
        overflow-y: auto
        max-height: calc(100dvh - 64px)
        scroll-padding-bottom: 16px

    .article-chat__stage
        display: flex
        flex-direction: column
        grid-template-rows: none

    .article-chat__top,
    .article-chat__center
        flex: 0 0 auto
        overflow: visible

    .article-chat__bottom
        flex: 0 0 auto
        padding-bottom: 4px

    .article-chat__greeting
        margin-bottom: 2px

    .article-chat__inner
        padding: 10px 12px 8px

    .article-chat__continue
        margin-top: 8px

    .article-chat__hint,
    .article-chat__empty
        overflow-wrap: anywhere

    .article-chat__chips
        flex-wrap: nowrap
        overflow-x: auto
        overscroll-behavior-x: contain
        padding-bottom: 6px
        scroll-padding-inline: 4px
        scrollbar-width: thin
        scrollbar-color: rgba(8, 135, 220, 0.35) transparent

        &::-webkit-scrollbar
            height: 4px

        &::-webkit-scrollbar-track
            background: transparent

        &::-webkit-scrollbar-thumb
            background: rgba(8, 135, 220, 0.35)
            border-radius: 999px

    .article-chat__secondary-row
        flex-direction: column
        align-items: stretch

    .article-chat__add
        width: 100%

    .article-chat__chip
        flex: 0 0 auto

    .article-chat__continue
        align-self: stretch
        width: 100%

    .article-chat__resume-actions
        flex-direction: column

@media (prefers-reduced-motion: reduce)
    .article-chat__dots span,
    .article-chat__suggest--wait::after
        animation: none
        opacity: 1

@keyframes article-chat-blink
    0%, 80%, 100%
        opacity: 0.25
    40%
        opacity: 1

// Rewrite H2 list: the step stays inside the viewport and only the list scrolls.
// 88px is the fixed app bar (64) plus the page container padding. Below 960px
// the app bar is 56px, so the slack is 80px.
.article-chat--sections
    min-height: 0
    height: calc(100dvh - 88px)
    max-height: calc(100dvh - 88px)
    overflow: hidden

    .article-chat__stage
        display: flex
        flex-direction: column
        flex: 1 1 auto
        min-height: 0
        overflow: hidden
        grid-template-rows: none

    .article-chat__top
        flex: 0 0 auto
        max-height: none
        overflow: hidden
        padding-bottom: 6px

    .article-chat__center
        flex: 1 1 auto
        min-height: 0
        overflow: hidden
        display: flex
        flex-direction: column

    .article-chat__bottom
        flex: 0 0 auto
        flex-direction: row
        flex-wrap: nowrap
        align-items: center
        gap: 8px 12px
        padding-top: 8px
        padding-bottom: 0
        background: var(--v-background-base)

    .article-chat__continue
        width: auto
        align-self: center
        margin-top: 0
        flex: 0 0 auto

    .article-chat__hint
        flex: 1 1 auto
        min-width: 0
        margin: 0
        white-space: nowrap
        overflow: hidden
        text-overflow: ellipsis

    .article-chat__card-title
        overflow: hidden
        text-overflow: ellipsis
        white-space: nowrap

    .article-chat__shell
        flex: 1 1 auto
        min-height: 0
        display: flex
        flex-direction: column
        overflow: hidden

    .article-chat__inner
        flex: 1 1 auto
        min-height: 0
        display: flex
        flex-direction: column
        overflow: hidden
        padding: 8px 12px 6px

    .article-chat__label
        flex: 0 0 auto
        margin-bottom: 4px

    .article-chat__bar
        margin-bottom: 0

    .article-chat__history-row
        flex-wrap: nowrap
        min-width: 0

        > span
            min-width: 0
            overflow: hidden
            text-overflow: ellipsis
            white-space: nowrap

    .article-chat__cards
        flex: 0 0 auto
        grid-template-columns: 1fr 1fr 1fr
        gap: 6px
        min-width: 0

    .article-chat__card
        min-width: 0
        padding: 8px 10px

    .article-chat__card-blurb
        display: none

    .article-chat__goal-blurb
        flex: 0 0 auto
        margin: 6px 0 0
        font-size: 0.9rem
        line-height: 1.35

    // The goal block is the flex child that receives the card's leftover height.
    // Without it, the H2 list's parent is height:auto and flex-basis:0 collapses
    // the checkboxes to a 0px frame.
    .article-chat__goal
        flex: 1 1 auto
        min-height: 0
        min-width: 0
        display: flex
        flex-direction: column
        overflow: hidden

    .article-chat__goal-sections
        flex: 1 1 auto
        min-height: 0
        min-width: 0
        display: flex
        flex-direction: column
        overflow: hidden

        .article-chat__loaded-kicker,
        .article-chat__empty
            flex: 0 0 auto

    // flex-basis:auto plus a 3-row floor. max-height shrinks the list only when
    // the window is short, so Pokračovat stays on screen and the page does not scroll.
    .article-chat__sections
        flex: 0 1 auto
        min-height: 132px
        max-height: calc(100dvh - 440px)
        overflow-x: hidden
        overflow-y: auto
        overscroll-behavior: contain

@media (max-width: 959px)
    .article-chat--sections
        height: calc(100dvh - 80px)
        max-height: calc(100dvh - 80px)
        overflow: hidden

@media (max-width: 600px)
    .article-chat--sections
        .article-chat__bar
            flex-wrap: nowrap
            align-items: center

        .article-chat__bar,
        .article-chat__crumb
            flex-wrap: nowrap

        .article-chat__crumb
            flex: 1 1 auto
            overflow: hidden

        .article-chat__crumb-link
            flex: 0 0 auto
            white-space: nowrap

        .article-chat__crumb > span:last-child
            overflow: hidden
            text-overflow: ellipsis
            white-space: nowrap

        .article-chat__progress
            flex: 0 1 120px

        .article-chat__cancel
            flex: 0 0 auto
            padding-left: 4px
            padding-right: 4px

        .article-chat__goal-blurb
            display: none

        .article-chat__top
            padding-bottom: 0

        .article-chat__bottom
            padding-top: 4px

        .article-chat__section
            padding-top: 4px
            padding-bottom: 4px

        .article-chat__goal-sections .article-chat__loaded-kicker
            margin-bottom: 0

        .article-chat__goal-sections .article-chat__empty
            margin-top: 4px

@media (max-height: 640px)
    .article-chat--sections
        .article-chat__top
            max-height: 6.5rem
            overflow: hidden

        .article-chat__question p
            display: -webkit-box
            -webkit-line-clamp: 2
            -webkit-box-orient: vertical
            overflow: hidden

@media (max-height: 520px)
    .article-chat--sections
        .article-chat__top
            max-height: 4.5rem
            overflow: hidden

        .article-chat__question p
            display: -webkit-box
            -webkit-line-clamp: 2
            -webkit-box-orient: vertical
            overflow: hidden

        .article-chat__sections
            min-height: 108px
            max-height: calc(100dvh - 300px)

        .article-chat__section
            min-height: 36px
            padding-top: 2px
            padding-bottom: 2px

@media (max-height: 450px)
    .article-chat--sections
        .article-chat__history
            display: none

        .article-chat__top
            max-height: 2.4rem

        .article-chat__question p
            -webkit-line-clamp: 1

        .article-chat__inner
            padding: 4px 8px 2px

        .article-chat__card
            padding: 4px 8px

        .article-chat__sections
            min-height: 108px
            max-height: calc(100dvh - 240px)

</style>
